import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, ShieldCheckIcon, FileSearchIcon, UsersIcon } from 'lucide-react';
const features = [{
  num: '01',
  icon: <EyeIcon className="w-8 h-8 md:w-10 md:h-10 text-slate-800" strokeWidth={1.5} data-id="element-344" />,
  title: 'Transparent operations',
  description: "We don't use unpredictable methods that make decisions behind your back. Every step our team takes is clearly defined, so you know exactly how your enrollments are being handled."
}, {
  num: '02',
  icon: <ShieldCheckIcon className="w-8 h-8 md:w-10 md:h-10 text-slate-800" strokeWidth={1.5} data-id="element-345" />,
  title: 'Dedicated account oversight',
  description: "You give our experts access to the systems your team already uses. We work within those boundaries and only do exactly what we agreed to do. You never have to worry about us messing up your accounts."
}, {
  num: '03',
  icon: <FileSearchIcon className="w-8 h-8 md:w-10 md:h-10 text-slate-800" strokeWidth={1.5} data-id="element-346" />,
  title: 'Full audit trail',
  description: 'Every single enrollment we process is logged and easy for you to review with your dedicated account manager at any time.'
}, {
  num: '04',
  icon: <UsersIcon className="w-8 h-8 md:w-10 md:h-10 text-slate-800" strokeWidth={1.5} data-id="element-347" />,
  title: 'Expert judgment when it counts',
  description: "Our operations professionals handle the repetitive data entry. But when a tricky situation comes up or something doesn't look right, our experienced team uses their expertise to figure it out."
}];
export function TrustSection() {
  return <section className="py-24 md:py-32 overflow-hidden" style={{
    backgroundColor: '#F7F8FA'
  }} data-id="element-348">
      <div className="max-w-7xl mx-auto px-6" data-id="element-349">
        {/* Intro Text */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-24" data-id="element-350">
          <span className="text-steel-600 font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-351">
            Reliability & Trust
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 leading-tight mb-8" data-id="element-352">
            Built for reliability, control, and trust
          </h2>
          <div className="text-lg md:text-xl text-slate-900/60 leading-relaxed space-y-6" data-id="element-353">
            <p data-id="element-354">
              When you're setting up someone's health insurance, there's no room for mistakes. That's why we built Mach to be a completely transparent and reliable operations partner.
            </p>
          </div>
        </motion.div>

        {/* Stacked Features */}
        <div className="flex flex-col" data-id="element-355">
          {features.map((feature, index) => {
          const isEven = index % 2 !== 0;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="relative py-12 md:py-20 border-b border-steel-300/50 last:border-b-0 group" data-id="element-356">
                {/* Large Background Number */}
                <div className={`absolute top-1/2 -translate-y-1/2 text-[12rem] md:text-[18rem] font-heading font-bold text-steel-200/40 select-none pointer-events-none z-0 transition-transform duration-700 group-hover:scale-105 ${isEven ? 'left-0 md:left-12' : 'right-0 md:right-12'}`} data-id="element-357">
                  {feature.num}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center relative z-10" data-id="element-358">
                  {/* Left Column */}
                  <div className={`flex flex-col justify-center ${!isEven ? 'md:border-r border-steel-300/50 md:pr-16' : 'order-2 md:order-1 md:pr-16'}`} data-id="element-359">
                    {!isEven ? <div className="flex items-center gap-6" data-id="element-360">
                        <div className="w-16 h-16 md:w-20 md:h-20 shrink-0 bg-white rounded-xl border border-steel-200 flex items-center justify-center shadow-sm" data-id="element-361">
                          {feature.icon}
                        </div>
                        <h3 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 leading-tight" data-id="element-362">
                          {feature.title}
                        </h3>
                      </div> : <p className="text-lg text-slate-900/60 leading-relaxed" data-id="element-363">
                        {feature.description}
                      </p>}
                  </div>

                  {/* Right Column */}
                  <div className={`flex flex-col justify-center ${isEven ? 'md:border-l border-steel-300/50 md:pl-16 order-1 md:order-2' : 'md:pl-16'}`} data-id="element-364">
                    {isEven ? <div className="flex items-center gap-6" data-id="element-365">
                        <div className="w-16 h-16 md:w-20 md:h-20 shrink-0 bg-white rounded-xl border border-steel-200 flex items-center justify-center shadow-sm" data-id="element-366">
                          {feature.icon}
                        </div>
                        <h3 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 leading-tight" data-id="element-367">
                          {feature.title}
                        </h3>
                      </div> : <p className="text-lg text-slate-900/60 leading-relaxed" data-id="element-368">
                        {feature.description}
                      </p>}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}